import { useEffect, useRef, useState } from "react";

import DiffWorker from "@/workers/diff.worker?worker";
import type {
  DiffChunk,
  DiffRequest,
  DiffResponse,
} from "@/workers/diff.types";

type DiffStatus = "pending" | "ready" | "error";

interface UseDiffWorkerResult {
  status: DiffStatus;
  chunks: DiffChunk[];
  hasAnyChange: boolean;
  error: string | null;
}

type ResolvedDiff = {
  oldContent: string;
  newContent: string;
  payload:
    | { ok: true; chunks: DiffChunk[]; hasAnyChange: boolean }
    | { ok: false; error: string };
};

let sharedWorker: Worker | null = null;

const getWorker = (): Worker => {
  sharedWorker ??= new DiffWorker();
  return sharedWorker;
};

export function useDiffWorker(
  oldContent: string,
  newContent: string,
): UseDiffWorkerResult {
  const [resolved, setResolved] = useState<ResolvedDiff | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const worker = getWorker();
    const currentId = ++requestIdRef.current;

    const handleMessage = (event: MessageEvent<DiffResponse>) => {
      const data = event.data;
      if (data.id !== currentId) return;
      if (data.ok) {
        setResolved({
          oldContent,
          newContent,
          payload: {
            ok: true,
            chunks: data.chunks,
            hasAnyChange: data.hasAnyChange,
          },
        });
      } else {
        setResolved({
          oldContent,
          newContent,
          payload: { ok: false, error: data.error },
        });
      }
    };

    const handleError = (event: ErrorEvent) => {
      if (currentId !== requestIdRef.current) return;
      setResolved({
        oldContent,
        newContent,
        payload: {
          ok: false,
          error: event.message || "Worker error",
        },
      });
    };

    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);

    const request: DiffRequest = { id: currentId, oldContent, newContent };
    worker.postMessage(request);

    return () => {
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
    };
  }, [oldContent, newContent]);

  const isStale =
    !resolved ||
    resolved.oldContent !== oldContent ||
    resolved.newContent !== newContent;

  if (isStale) {
    return { status: "pending", chunks: [], hasAnyChange: false, error: null };
  }

  if (resolved.payload.ok) {
    return {
      status: "ready",
      chunks: resolved.payload.chunks,
      hasAnyChange: resolved.payload.hasAnyChange,
      error: null,
    };
  }

  return {
    status: "error",
    chunks: [],
    hasAnyChange: false,
    error: resolved.payload.error,
  };
}
